/**
 * Campaigns / ads response PROJECTIONS — hand-composed from generated atoms.
 *
 * Three response surfaces live here (one barrel namespace, so names are
 * distinctively prefixed):
 *
 *  - META CAMPAIGNS (`meta-campaigns/*`) — campaigns are fetched LIVE from the
 *    Meta API, not a DB table, so their entity + insights shapes are
 *    hand-modelled `z.object`s (no atom). Config extras join off
 *    `meta_campaign_config`.
 *  - META ADS (`meta-ads/*`) — the `meta_ad` atom, narrowed for the wire
 *    (`callToAction` non-null, `targetingOverride` typed, optional joined
 *    video / graphic / services).
 *  - MESSAGING CAMPAIGNS (`campaigns/*`) — the bulk SMS/email/WhatsApp blast
 *    product: `campaign` / `campaign_message` / `campaign_recipient` / `segment`
 *    / `suppression` / `org_sms_number` atoms plus computed list / analytics /
 *    preview shapes.
 *
 * Pure Zod, composed with `z.object` / `.extend` / `z.array` / `z.record`. Dates
 * are ISO strings (atoms already wire-shaped). See ./leads.ts for the pattern.
 */
import { z } from 'zod';
import { campaignAtomSchema, campaignMessageAtomSchema, campaignRecipientAtomSchema, metaAdAtomSchema, metaAdServiceAtomSchema, offerAtomSchema, orgSmsNumberAtomSchema, segmentAtomSchema, suppressionAtomSchema, whatsappTemplateAtomSchema, } from '../generated/index.js';
// ============================================================================
// META CAMPAIGNS — live from Meta API (no backing DB table)
// ============================================================================
/**
 * A campaign as returned by `GET /meta-campaigns` — the live Meta campaign
 * (`MetaCampaignData`) merged with local ad-count / creative-thumbnail
 * enrichment and the optional `meta_campaign_config` extras. `status` /
 * `objective` are free-text on the wire (Meta returns raw strings), so they
 * stay `z.string()` — not narrowed to the DB enums.
 */
export const metaCampaignListEntrySchema = z.object({
    // MetaCampaignData (live from Meta)
    id: z.string(),
    name: z.string(),
    status: z.string(),
    effectiveStatus: z.string(),
    objective: z.string(),
    dailyBudget: z.string().optional(),
    lifetimeBudget: z.string().optional(),
    createdTime: z.string().optional(),
    updatedTime: z.string().optional(),
    // Local enrichment
    adCount: z.number(),
    currency: z.string().optional(),
    previewImageUrl: z.string().nullable(),
    // Optional meta_campaign_config extras
    followUpType: z.string().optional(),
    conversionDestination: z.string().nullable().optional(),
    destinationType: z.string().nullable().optional(),
    locationId: z.string().nullable().optional(),
    targeting: z.record(z.string(), z.unknown()).nullable().optional(),
    metaAdSetId: z.string().nullable().optional(),
});
/** `GET /meta-campaigns` — the `{ campaigns }` wrapper (bare, no pagination). */
export const listMetaCampaignsResponseSchema = z.object({
    campaigns: z.array(metaCampaignListEntrySchema),
});
/** `POST /meta-campaigns` — the created Meta campaign + ad set ids. */
export const createMetaCampaignResponseSchema = z.object({
    metaCampaignId: z.string(),
    metaAdSetId: z.string(),
    followUpType: z.string(),
    conversionDestination: z.string().optional(),
    /**
     * The BRANCH the campaign targets, echoed back because the caller does not
     * choose the coordinates any more — it names a branch (or names nothing and
     * gets the default one), so this is how a confirmation card can state what
     * was actually targeted rather than what was asked for.
     */
    location: z
        .object({
        id: z.string(),
        label: z.string(),
    })
        .optional(),
});
/** Aggregate performance totals for a campaign (spend/cpc/cpm in cents). */
export const campaignInsightsTotalsSchema = z.object({
    impressions: z.number(),
    reach: z.number(),
    clicks: z.number(),
    spend: z.number(),
    cpc: z.number(),
    cpm: z.number(),
    ctr: z.number(),
    frequency: z.number(),
    leads: z.number(),
    // `conversions` is the legacy purchase count. Keep lead sources separate.
    conversions: z.number(),
    onsiteLeads: z.number().optional(),
    websiteLeads: z.number().optional(),
    // Optional while the dashboard and API are deployed independently.
    messagingConversations: z.number().optional(),
    linkClicks: z.number().optional(),
});
/** Per-ad performance breakdown within a campaign's insights. */
export const campaignInsightsAdRowSchema = z.object({
    adId: z.string(),
    adName: z.string(),
    impressions: z.number(),
    reach: z.number(),
    clicks: z.number(),
    spend: z.number(),
    cpc: z.number(),
    cpm: z.number(),
    ctr: z.number(),
    // Older API deployments omit these. Missing is unavailable, not zero.
    frequency: z.number().optional(),
    leads: z.number().optional(),
    conversions: z.number().optional(),
    onsiteLeads: z.number().optional(),
    websiteLeads: z.number().optional(),
    messagingConversations: z.number().optional(),
    linkClicks: z.number().optional(),
});
/** `GET /meta-campaigns/:id/insights` — one campaign's insights from Meta. */
export const metaCampaignInsightsSchema = z.object({
    metaCampaignId: z.string(),
    // ISO code of the ad account that spent the money; older APIs omit it.
    currency: z.string().optional(),
    dateRange: z.object({ since: z.string(), until: z.string() }).nullable(),
    totals: campaignInsightsTotalsSchema,
    ads: z.array(campaignInsightsAdRowSchema),
});
/** Insights for a single campaign in the batched response. */
export const campaignInsightsSummarySchema = z.object({
    metaCampaignId: z.string(),
    // ISO code of the ad account that spent the money; older APIs omit it.
    currency: z.string().optional(),
    totals: campaignInsightsTotalsSchema,
});
/** `GET /meta-campaigns/insights` — batched insights for every campaign. */
export const listCampaignInsightsResponseSchema = z.object({
    dateRange: z.object({ since: z.string(), until: z.string() }).nullable(),
    insights: z.array(campaignInsightsSummarySchema),
});
/**
 * `POST /meta-campaigns/:id/pause` — pause acknowledgement with the campaign
 * `effective_status` READ BACK from Meta after the mutation (ADR-005).
 * `campaignEffectiveStatus` is null / `verified` false when the read-back
 * failed — consumers must then report "submitted, unverified", not "paused".
 */
export const pauseCampaignResponseSchema = z.object({
    paused: z.literal(true),
    campaignEffectiveStatus: z.string().nullable().optional(),
    verified: z.boolean().optional(),
});
/**
 * `POST /meta-campaigns/:id/resume` — resume acknowledgement. `status` is the
 * read-back `effective_status` (or `'UNVERIFIED'`), never an assumed 'ACTIVE'.
 */
export const resumeCampaignResponseSchema = z.object({
    metaCampaignId: z.string(),
    status: z.string(),
    campaignEffectiveStatus: z.string().nullable().optional(),
    verified: z.boolean().optional(),
});
/**
 * `PUT /meta-campaigns/:id` — update acknowledgement with the campaign
 * `effective_status` read back from Meta after the mutation (ADR-005).
 */
export const updateMetaCampaignResponseSchema = z.object({
    updated: z.literal(true),
    campaignEffectiveStatus: z.string().nullable().optional(),
    verified: z.boolean().optional(),
});
/** `POST /meta-campaigns/:id/duplicate` — the enqueue acknowledgement. */
export const queueDuplicateCampaignResponseSchema = z.object({
    queued: z.boolean(),
});
/**
 * `POST /meta-ads/:id/pause` — pause acknowledgement with the ad
 * `effective_status` READ BACK from Meta after the mutation (ADR-005).
 * `adEffectiveStatus` is null / `verified` false when the read-back
 * failed — consumers must then report "submitted, unverified", not "paused".
 */
export const pauseAdResponseSchema = z.object({
    paused: z.literal(true),
    adEffectiveStatus: z.string().nullable().optional(),
    verified: z.boolean().optional(),
});
/**
 * `POST /meta-ads/:id/resume` — resume acknowledgement. `status` is the
 * read-back `effective_status` (or `'UNVERIFIED'`), never an assumed 'ACTIVE'.
 */
export const resumeAdResponseSchema = z.object({
    adId: z.string(),
    status: z.string(),
    adEffectiveStatus: z.string().nullable().optional(),
    verified: z.boolean().optional(),
});
// ============================================================================
// META ADS — the meta_ad atom, narrowed for the wire
// ============================================================================
/**
 * Ad targeting override (`meta_ad.targeting_override`, a `$type<MetaTargeting>`
 * jsonb the generator widens to `z.unknown()`). Hand-narrowed to the
 * `MetaTargeting` shape so the projection matches the api-client `Ad` type.
 */
export const campaignTargetingSchema = z.object({
    location: z.string().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    distanceKm: z.number().optional(),
    locationExpansion: z.boolean().optional(),
    ageMin: z.number().optional(),
    ageMax: z.number().optional(),
    genders: z.array(z.number()).optional(),
    countries: z.array(z.string()).optional(),
});
/** Ad media preview; Meta-only thumbnails do not have a local video ID. */
export const adVideoSchema = z.object({
    // The existing video envelope also carries owned uploaded still images.
    // Optional preserves the response contract for the legacy rollout cohort.
    mediaType: z.enum(['image', 'video']).nullable().optional(),
    id: z.string().optional(),
    title: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    videoUrl: z.string().nullable(),
    duration: z.number().nullable(),
    // Intrinsic size, set for asset-backed creatives. Zod STRIPS unknown keys,
    // so a field missing here never reaches the client no matter what the
    // service returns.
    width: z.number().nullable().optional(),
    height: z.number().nullable().optional(),
});
/** Saved offer linked by an owned graphic, not a name-based offer match. */
export const adGraphicOfferSchema = offerAtomSchema.pick({
    id: true,
    name: true,
    description: true,
    state: true,
    validFrom: true,
    validUntil: true,
    discountType: true,
    discountPercent: true,
    discountAmountCents: true,
    offerPriceCents: true,
    buyQuantity: true,
    getQuantity: true,
    limitPerClient: true,
    redemptionLimit: true,
});
/**
 * An ad as returned by the ad endpoints — the `meta_ad` atom with wire
 * narrowings: `callToAction` is non-null (DB defaults to `LEARN_MORE`),
 * `targetingOverride` is the typed `MetaTargeting`, plus optionally-joined
 * `video`, `graphicImageUrl` and `services`.
 */
export const adSchema = metaAdAtomSchema
    .omit({ callToAction: true, targetingOverride: true })
    .extend({
    callToAction: metaAdAtomSchema.shape.callToAction.unwrap(),
    targetingOverride: campaignTargetingSchema.nullable(),
    video: adVideoSchema.optional(),
    graphicImageUrl: z.string().nullable().optional(),
    graphicImageWidth: z.number().nullable().optional(),
    graphicImageHeight: z.number().nullable().optional(),
    graphicOfferId: z.string().nullable().optional(),
    graphicOffer: adGraphicOfferSchema.nullable().optional(),
    services: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        // How the link was made: `auto` = the ad-link matcher's guess, shown
        // as "Matched automatically" so the clinic can confirm or fix it.
        linkSource: metaAdServiceAtomSchema.shape.linkSource.optional(),
    }))
        .optional(),
});
/** POST /meta-ads creates a draft under its required parent campaign. */
export const createAdResponseSchema = adSchema
    .pick({
    id: true,
    name: true,
    status: true,
    metaCampaignId: true,
})
    .extend({ metaCampaignId: adSchema.shape.metaCampaignId.unwrap() });
/** `GET /meta-ads/campaigns/:metaCampaignId` — list of ads for a campaign. */
export const listAdsResponseSchema = z.object({
    ads: z.array(adSchema),
    total: z.number(),
});
/** `POST /meta-ads/:id/sync` — the synced ad plus the sync flag. */
export const syncAdResponseSchema = z.object({
    ad: adSchema,
    synced: z.boolean(),
});
/** `POST /meta-ads/import` — import outcome counters. */
export const importAdsResponseSchema = z.object({
    imported: z.number(),
    skipped: z.number(),
    total: z.number(),
});
// ============================================================================
// MESSAGING CAMPAIGNS — bulk SMS/email/WhatsApp blasts
// ============================================================================
/** A messaging-campaign header row — the atom, verbatim. */
export const messagingCampaignSchema = campaignAtomSchema;
/**
 * A per-channel message body attached to a campaign — the atom with
 * `whatsappTemplateParams` narrowed from jsonb-`unknown` to the ordered
 * {{1}}..{{n}} template param strings.
 */
export const campaignMessageSchema = campaignMessageAtomSchema.extend({
    whatsappTemplateParams: z.array(z.string()).nullable(),
});
/** A materialized send (lead × channel) ledger row — the atom, verbatim. */
export const campaignRecipientSchema = campaignRecipientAtomSchema;
/**
 * The persisted audience filter (`segment.filter_json`, a `$type<SegmentFilter>`
 * jsonb the generator widens to `z.unknown()`). Hand-narrowed to match.
 */
export const segmentFilterSchema = z.object({
    status: z.array(z.string()).optional(),
    source: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    search: z.string().optional(),
    consentEmail: z.boolean().optional(),
    consentSms: z.boolean().optional(),
    createdFrom: z.string().optional(),
    createdTo: z.string().optional(),
    lastContactedBefore: z.string().optional(),
    lastContactedAfter: z.string().optional(),
});
/** An audience segment — the atom with `filterJson` narrowed to `SegmentFilter`. */
export const segmentSchema = segmentAtomSchema.extend({
    filterJson: segmentFilterSchema,
});
/**
 * `GET /campaigns/entitlements` — which channels the org's plan allows.
 *
 * `checkChannelEntitlement` returns `{ allowed, blocked }`, and
 * `blocked[].reason` is a reason CODE rather than prose — the service's only
 * member is `ChannelBlockReasons.REQUIRES_PAID_PLAN`. Modelled as an enum, not
 * `z.string()`, so adding a block reason without teaching the callers about it
 * fails here instead of rendering a raw code at an operator.
 */
export const channelEntitlementsResponseSchema = z.object({
    allowed: z.array(campaignMessageAtomSchema.shape.channel),
    blocked: z.array(z.object({
        channel: campaignMessageAtomSchema.shape.channel,
        reason: z.enum(['requires_paid_plan']),
    })),
});
/**
 * A WhatsApp message template mirrored from the org's linked WABA — the atom,
 * verbatim. `syncWhatsappTemplates` reads the cache with a bare `findMany` (no
 * column projection), so the whole row is what reaches the wire.
 */
export const whatsappTemplateSchema = whatsappTemplateAtomSchema;
/** `GET /campaigns/whatsapp-templates` — `{ templates, synced }`. */
export const listWhatsappTemplatesResponseSchema = z.object({
    templates: z.array(whatsappTemplateSchema),
    /** True when this call re-synced from Meta (vs a pure cache read). */
    synced: z.boolean(),
});
/** A per-channel suppression (opt-out / bounce) entry — the atom, verbatim. */
export const suppressionSchema = suppressionAtomSchema;
/** The org's campaign SMS sender number — the atom, verbatim. */
export const smsNumberSchema = orgSmsNumberAtomSchema;
/**
 * `GET /campaigns/sms-number` — the org's number, or `null` when none is
 * provisioned.
 */
export const smsNumberOrNullSchema = smsNumberSchema.nullable();
/** A purchasable Twilio number (`GET /campaigns/sms-number/available`). */
export const availableSmsNumberSchema = z.object({
    phoneNumber: z.string(),
    friendlyName: z.string(),
    locality: z.string().optional(),
    region: z.string().optional(),
});
/** `GET /campaigns/sms-number/available` — the bare array of numbers. */
export const availableSmsNumbersResponseSchema = z.array(availableSmsNumberSchema);
/** `GET /campaigns/:id` — a campaign with its per-channel messages. */
export const messagingCampaignWithMessagesSchema = campaignAtomSchema.extend({
    messages: z.array(campaignMessageSchema),
});
/** A campaign list row — the campaign plus audience name + recipient counts. */
export const messagingCampaignListItemSchema = campaignAtomSchema.extend({
    segmentName: z.string().nullable(),
    recipientCounts: z.record(z.string(), z.number()),
});
/** `GET /campaigns` — list projection: `{ items, total, limit, offset }`. */
export const messagingCampaignListResponseSchema = z.object({
    items: z.array(messagingCampaignListItemSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
/** `GET /campaigns/segments` — list projection. */
export const segmentListResponseSchema = z.object({
    items: z.array(segmentSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
/** `POST /campaigns/segments/preview` — reach total + per-channel counts. */
export const segmentPreviewResponseSchema = z.object({
    excludedNoMarketingBasis: z.number().default(0),
    total: z.number(),
    reachable: z.number(),
    channels: z.record(z.string(), z.number()),
});
/** `POST /campaigns/:id/launch` — materialize + enqueue summary. */
export const launchCampaignResponseSchema = z.object({
    campaignId: z.string(),
    materialized: z.number(),
    enqueued: z.number(),
});
/** Messaging campaign lifecycle acknowledgements, matching each service result. */
export const campaignLifecycleResponseSchema = z.union([
    launchCampaignResponseSchema,
    z.object({ campaignId: z.string(), enqueued: z.number() }),
    z.object({ campaignId: z.string(), cancelled: z.literal(true) }),
    z.object({ campaignId: z.string(), paused: z.literal(true) }),
]);
/** `GET /campaigns/:id/analytics` — funnel counts derived from recipient rows. */
export const campaignAnalyticsSchema = z.object({
    total: z.number(),
    sent: z.number(),
    delivered: z.number(),
    failed: z.number(),
    optedOut: z.number(),
    opened: z.number(),
    clicked: z.number(),
});
/** One per-recipient outcome row (`GET /campaigns/:id/recipients`). */
export const campaignRecipientRowSchema = z.object({
    id: z.string(),
    channel: campaignRecipientAtomSchema.shape.channel,
    status: z.string(),
    error: z.string().nullable(),
    contact: z.string().nullable(),
    name: z.string().nullable(),
    sentAt: z.string().datetime().nullable(),
});
/** `GET /campaigns/:id/recipients` — the bare array of recipient rows. */
export const listCampaignRecipientsResponseSchema = z.array(campaignRecipientRowSchema);
//# sourceMappingURL=campaigns.js.map